import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowLeft, X, ShieldCheck, Eye, EyeOff, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mauritian local number: 7 (fixed) or 8 (mobile) digits.
const MAURITIUS_LOCAL_RE = /^\d{7,8}$/;
// Generic fallback for any other country code: 6+ digits.
const GENERIC_LOCAL_RE  = /^\d{6,15}$/;

export default function FirstTimeOnboarding({ demoToken, onComplete, onCancel }) {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Super Admin role id (resolved by name once, not hardcoded)
  const [superAdminRoleId, setSuperAdminRoleId] = useState(null);

  // Step 1 — Restaurant (company): saved to restaurant_company.
  const [restaurantForm, setRestaurantForm] = useState({
    name: '',
    address: '',
    city: '',
    phone: '',
    email: '',
  });
  const [newRestaurantId, setNewRestaurantId] = useState(null);

  // Step 2 — Location: the first branch under the restaurant (restaurant_location).
  const [locationForm, setLocationForm] = useState({
    name: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: '',
  });
  // eslint-disable-next-line no-unused-vars
  const [newLocationId, setNewLocationId] = useState(null);

  // Step 3 — Super-Admin owner account (restaurant_user).
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    country_code: '+230',  // default to Mauritius
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  // Country dial codes from masters, falling back to a Mauritius-only option when the master is empty.
  const [countryCodes, setCountryCodes] = useState([{ code: 'MU', name: 'Mauritius', dial_code: '+230' }]);

  // Resolve the Super Admin role id by name + fetch country dial codes.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/users/login/roles/`);
        const data = await res.json();
        const roles = data?.data?.roles || [];
        const sa = roles.find((r) => r.name === 'Super Admin');
        if (sa) setSuperAdminRoleId(sa.id);
      } catch (_) { /* non-fatal; fallback to id=1 below */ }

      try {
        const cRes = await fetch(`${API_URL}/users/masters/countries/`);
        const cData = await cRes.json();
        const list = (cData?.results || []).filter((c) => c.dial_code);
        if (list.length) {
          // Mauritius first if present; otherwise alphabetical.
          const mu = list.find((c) => c.dial_code === '+230');
          const rest = list.filter((c) => c.dial_code !== '+230').sort((a, b) => a.name.localeCompare(b.name));
          setCountryCodes(mu ? [mu, ...rest] : list);
        }
      } catch (_) { /* keep the Mauritius fallback */ }
    })();
  }, []);

  const authHeader = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${demoToken}`,
  });

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserForm((p) => ({ ...p, [name]: value }));
    if (error) setError('');
  };

  const handleRestaurantChange = (e) => {
    const { name, value } = e.target;
    setRestaurantForm((p) => ({ ...p, [name]: value }));
    if (error) setError('');
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setLocationForm((p) => ({ ...p, [name]: value }));
    if (error) setError('');
  };

  // Step 1 → create the restaurant (restaurant_company), then advance to Location.
  const createRestaurant = async () => {
    if (!restaurantForm.name.trim()) {
      setError('Restaurant name is required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const rRes = await fetch(`${API_URL}/users/restaurants/create/`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          name: restaurantForm.name,
          address: restaurantForm.address,
          city: restaurantForm.city,
          phone: restaurantForm.phone,
          email: restaurantForm.email,
        }),
      });
      const rData = await rRes.json();
      if (!rRes.ok || !rData.success) {
        setError(rData.error || 'Failed to create restaurant');
        return;
      }
      setNewRestaurantId(rData.data.id);
      // Convenience: prefill the first location from the restaurant basics.
      setLocationForm((p) => ({
        ...p,
        name: p.name || `${restaurantForm.name} — Main`,
        city: p.city || restaurantForm.city,
        address_line1: p.address_line1 || restaurantForm.address,
        phone: p.phone || restaurantForm.phone,
      }));
      setStep(2);
    } catch (e) {
      setError('Network error — please retry');
    } finally {
      setBusy(false);
    }
  };

  // Step 2 → create the first location (restaurant_location), marked primary.
  const createLocation = async () => {
    if (!newRestaurantId) {
      setError('Please create the restaurant first');
      return;
    }
    if (!locationForm.name.trim()) {
      setError('Location name is required');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/users/restaurant/${newRestaurantId}/locations/`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          name: locationForm.name,
          address_line1: locationForm.address_line1,
          city: locationForm.city,
          state: locationForm.state,
          postal_code: locationForm.postal_code,
          country: locationForm.country,
          phone: locationForm.phone,
          is_primary: true,   // the first location is the primary branch
          is_active: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to create location');
        return;
      }
      setNewLocationId(data.data.id);
      setStep(3);
    } catch (e) {
      setError('Network error — please retry');
    } finally {
      setBusy(false);
    }
  };

  // Step 3 creates the Super-Admin owner under the new restaurant, then auto signs in. No OTP step.
  const createUserAndFinish = async () => {
    const req = ['first_name', 'last_name', 'email', 'username', 'password', 'phone'];
    const missing = req.filter((k) => !userForm[k]?.toString().trim());
    if (missing.length) {
      setError(`Please fill all required fields: ${missing.join(', ')}`);
      return;
    }
    if (!EMAIL_RE.test(userForm.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    const localPhone = userForm.phone.replace(/\s+/g, '');  // strip spaces, allow only digits
    if (!/^\d+$/.test(localPhone)) {
      setError('Phone number can only contain digits');
      return;
    }
    if (userForm.country_code === '+230') {
      if (!MAURITIUS_LOCAL_RE.test(localPhone)) {
        setError('Mauritius phone numbers are 7 or 8 digits (e.g. 5 123 4567).');
        return;
      }
    } else if (!GENERIC_LOCAL_RE.test(localPhone)) {
      setError('Phone number is too short or too long');
      return;
    }
    if ((userForm.password || '').length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const fullPhone = `${userForm.country_code} ${localPhone}`;

    setBusy(true);
    setError('');
    try {
      // 1. Create the owner, linked to the real restaurant + Super Admin role.
      const res = await fetch(`${API_URL}/users/user/create/`, {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({
          restaurant_id: newRestaurantId,
          staff_type_id: superAdminRoleId || 1,  // resolved by name, fallback to 1
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          email: userForm.email.trim(),
          username: userForm.username.trim(),
          password: userForm.password,
          phone: fullPhone,
          employee_id: `OWN-${Date.now().toString().slice(-6)}`,
          hire_date: new Date().toISOString().slice(0, 10),
          employment_status: 'ACTIVE',
          is_active: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        // The backend returns specific messages for email/username/phone duplicates.
        setError(data.error || 'Failed to create user');
        return;
      }

      // 2. Auto sign-in as the new Super Admin.
      const lRes = await fetch(`${API_URL}/users/login/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userForm.email.trim(), password: userForm.password }),
      });
      const lData = await lRes.json();
      if (!lRes.ok || !lData.success) {
        setError(lData.error || 'Account created, but auto sign-in failed. Please log in manually.');
        return;
      }

      // 3. Start a session row for the new user (non-blocking).
      try {
        await fetch(`${API_URL}/users/session/start/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${lData.data.token}`,
          },
          body: JSON.stringify({ user_id: lData.data.user.id }),
        });
      } catch (sessErr) {
        console.warn('Session start failed for new user:', sessErr);
      }

      onComplete(lData.data.user, { token: lData.data.token });
    } catch (e) {
      setError('Network error — please retry');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] overflow-y-auto bg-[#0a0807]">
      <style>{`
        /* Type comes from the app-wide --font-* tokens (src/styles/index.css)
           — the ceremony here is lighting and colour, not a different font. */
        .ob-root {
          font-family: var(--font-sans);
          color: #f5f0e1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        .ob-serif { font-family: var(--font-display); }

        @keyframes ob-fade-up {
          0%   { opacity: 0; transform: translateY(14px); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }
        @keyframes ob-key-breathe {
          0%, 100% { opacity: 0.55; transform: translate(-50%,-50%) scale(1); }
          50%      { opacity: 0.85; transform: translate(-50%,-50%) scale(1.08); }
        }
        .ob-fade { animation: ob-fade-up 0.7s cubic-bezier(.2,.7,.2,1) both; }
        .ob-keylight {
          position: fixed; left: 50%; top: 50%;
          width: 80vw; height: 80vw; max-width: 1100px; max-height: 1100px;
          border-radius: 9999px;
          background: radial-gradient(circle,
            rgba(244,208,63,0.18) 0%,
            rgba(212,175,55,0.10) 25%,
            rgba(212,175,55,0.04) 50%,
            transparent 70%);
          filter: blur(40px);
          transform: translate(-50%,-50%);
          animation: ob-key-breathe 6s ease-in-out infinite;
          pointer-events: none;
        }
        .ob-grain {
          position: fixed; inset: -50%;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          opacity: 0.4; mix-blend-mode: overlay; pointer-events: none;
        }

        .ob-tag {
          font-size: 10px; font-weight: 600; letter-spacing: 0.45em;
          text-transform: uppercase; color: #d4af37;
        }
        .ob-h1 {
          font-family: var(--font-display);
          font-weight: 500; font-size: clamp(34px, 5vw, 52px); line-height: 1.1;
          color: #ffffff;
          letter-spacing: -0.005em;
        }
        .ob-sub { font-size: 14px; font-weight: 400; color: rgba(245,240,225,0.75); letter-spacing: 0.015em; }

        .ob-label {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.34em;
          text-transform: uppercase;
          color: #d4af37;
          display: block;
          margin-bottom: 9px;
        }
        .ob-input {
          width: 100%;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.16);
          color: #ffffff;
          padding: 13px 15px;
          border-radius: 2px;
          font-family: var(--font-sans);
          font-size: 15px;
          font-weight: 500;
          letter-spacing: 0.015em;
          outline: none;
          transition: all 0.25s ease;
          -webkit-font-smoothing: antialiased;
        }
        .ob-input::placeholder {
          color: rgba(245,240,225,0.42);
          font-weight: 400;
        }
        .ob-input:focus {
          background: rgba(255,255,255,0.05);
          border-color: rgba(212,175,55,0.75);
          box-shadow: 0 0 0 3px rgba(212,175,55,0.10);
        }
        .ob-input:-webkit-autofill,
        .ob-input:-webkit-autofill:hover,
        .ob-input:-webkit-autofill:focus {
          -webkit-text-fill-color: #ffffff !important;
          -webkit-box-shadow: 0 0 0 1000px rgba(14,12,10,0.98) inset !important;
          caret-color: #d4af37 !important;
          transition: background-color 9999s ease-in-out 0s;
          font-family: var(--font-sans) !important;
        }

        .ob-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 10px;
          padding: 13px 24px;
          background: linear-gradient(180deg, #c9a449 0%, #a37e29 100%);
          color: #0a0807;
          font-weight: 600; font-size: 11px;
          letter-spacing: 0.34em; text-transform: uppercase;
          border: none; border-radius: 2px;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow:
            0 1px 0 rgba(255,255,255,0.18) inset,
            0 -1px 0 rgba(0,0,0,0.20) inset,
            0 8px 24px -10px rgba(212,175,55,0.35);
        }
        .ob-btn:hover:not(:disabled) {
          background: linear-gradient(180deg, #d4af37 0%, #b8860b 100%);
          box-shadow:
            0 1px 0 rgba(255,255,255,0.22) inset,
            0 -1px 0 rgba(0,0,0,0.22) inset,
            0 12px 32px -10px rgba(212,175,55,0.45);
        }
        .ob-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .ob-btn-ghost {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 12px 18px; background: transparent;
          border: 1px solid rgba(255,255,255,0.12);
          color: rgba(245,240,225,0.7);
          font-size: 10px; font-weight: 500;
          letter-spacing: 0.32em; text-transform: uppercase;
          border-radius: 2px; cursor: pointer;
          transition: all 0.25s ease;
        }
        .ob-btn-ghost:hover:not(:disabled) {
          border-color: rgba(212,175,55,0.55);
          color: #d4af37;
        }
        .ob-btn-ghost:disabled { opacity: 0.4; cursor: not-allowed; }

        .ob-card {
          background: linear-gradient(180deg, rgba(18,16,12,0.85), rgba(8,7,6,0.92));
          border: 1px solid rgba(255,255,255,0.06);
          backdrop-filter: blur(20px) saturate(130%);
          -webkit-backdrop-filter: blur(20px) saturate(130%);
          border-radius: 4px;
          box-shadow: 0 40px 100px -30px rgba(0,0,0,0.9);
        }

        .ob-step-dot {
          width: 28px; height: 28px; border-radius: 9999px;
          display: inline-flex; align-items: center; justify-content: center;
          border: 1px solid rgba(255,255,255,0.18);
          font-family: var(--font-display);
          font-size: 14px; font-weight: 600;
          color: rgba(245,240,225,0.45);
          transition: all 0.3s ease;
        }
        .ob-step-dot.active {
          border-color: #d4af37; color: #d4af37;
          background: rgba(212,175,55,0.08);
          box-shadow: 0 0 16px rgba(212,175,55,0.25);
        }
        .ob-step-dot.done {
          border-color: rgba(212,175,55,0.7); background: rgba(212,175,55,0.18);
          color: #f5f0e1;
        }
        .ob-step-rule {
          height: 1px; width: 60px;
          background: linear-gradient(90deg, rgba(212,175,55,0.5), rgba(255,255,255,0.08));
        }
        .ob-step-label {
          font-size: 10px; font-weight: 500; letter-spacing: 0.32em;
          text-transform: uppercase;
        }

        .ob-cuisine {
          position: relative;
          padding: 22px 16px 18px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.25s ease;
          text-align: center;
        }
        .ob-cuisine:hover {
          border-color: rgba(212,175,55,0.45);
          background: rgba(212,175,55,0.04);
          transform: translateY(-2px);
        }
        .ob-cuisine.selected {
          border-color: #d4af37;
          background: rgba(212,175,55,0.10);
          box-shadow: 0 0 24px rgba(212,175,55,0.18), 0 1px 0 rgba(212,175,55,0.4);
        }
        .ob-cuisine-icon {
          width: 40px; height: 40px; margin: 0 auto 10px;
          display: inline-flex; align-items: center; justify-content: center;
          color: rgba(212,175,55,0.7);
        }
        .ob-cuisine.selected .ob-cuisine-icon { color: #f4d03f; }
        .ob-cuisine-label {
          font-family: var(--font-display);
          font-size: 18px; font-weight: 600;
          color: #f5f0e1; line-height: 1.2;
        }
        .ob-cuisine-blurb {
          font-size: 10px; font-weight: 400;
          letter-spacing: 0.08em;
          color: rgba(245,240,225,0.45);
          margin-top: 4px;
        }

        .ob-cred-row {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 12px 16px;
          background: rgba(212,175,55,0.05);
          border: 1px solid rgba(212,175,55,0.20);
          border-radius: 2px;
        }
        .ob-cred-label {
          font-size: 9px; font-weight: 500; letter-spacing: 0.4em;
          text-transform: uppercase; color: rgba(212,175,55,0.7);
          min-width: 80px;
        }
        .ob-cred-value {
          font-family: var(--font-display);
          font-size: 16px; font-weight: 500;
          color: #f5f0e1; flex: 1; word-break: break-all;
        }
        .ob-copy {
          background: transparent; border: none; cursor: pointer;
          color: rgba(212,175,55,0.6); padding: 4px;
          transition: color 0.2s ease;
        }
        .ob-copy:hover { color: #f4d03f; }

        .ob-error {
          padding: 10px 14px;
          background: rgba(255, 80, 80, 0.08);
          border: 1px solid rgba(255, 80, 80, 0.25);
          border-radius: 2px;
          color: #ffb3b3;
          font-size: 13px;
        }

        .ob-back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 2px;
          color: rgba(245,240,225,0.65);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.34em;
          text-transform: uppercase;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .ob-back-link:hover:not(:disabled) {
          border-color: rgba(212,175,55,0.55);
          color: #d4af37;
        }
        .ob-back-link:disabled { opacity: 0.4; cursor: not-allowed; }

        .ob-eye-btn {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: rgba(212, 175, 55, 0.6);
          cursor: pointer;
          padding: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }
        .ob-eye-btn:hover { color: #f4d03f; }

        select.ob-input {
          background-color: rgba(14, 12, 10, 0.85);
          color: #ffffff;
          appearance: none;
          -webkit-appearance: none;
          cursor: pointer;
        }
        select.ob-input option {
          background-color: #0f0d0a;
          color: #f5f0e1;
        }

        .ob-checkbox {
          appearance: none;
          width: 14px; height: 14px;
          border: 1px solid rgba(212,175,55,0.5);
          background: transparent;
          border-radius: 2px;
          cursor: pointer;
          position: relative;
          flex-shrink: 0;
        }
        .ob-checkbox:checked {
          background: #d4af37;
          border-color: #d4af37;
        }
        .ob-checkbox:checked::after {
          content: '';
          position: absolute;
          left: 4px; top: 1px;
          width: 4px; height: 8px;
          border: solid #0a0807;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      `}</style>

      <div className="ob-keylight" />
      <div className="ob-grain" />

      <div className="ob-root relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Top bar: back to login */}
        <div className="ob-fade absolute top-0 left-0 right-0 flex items-center justify-between px-6 sm:px-10 py-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="ob-back-link"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
            <span>Back to Login</span>
          </button>
          <div className="text-[10px] font-medium tracking-[0.4em] uppercase text-white/40">
            Guided Setup
          </div>
        </div>

        {/* Step indicator */}
        <div className="ob-fade flex items-center gap-4 mb-10 mt-8">
          <div className={`ob-step-dot ${step === 1 ? 'active' : 'done'}`}>1</div>
          <div className="ob-step-rule" />
          <div className={`ob-step-dot ${step === 2 ? 'active' : step > 2 ? 'done' : ''}`}>2</div>
          <div className="ob-step-rule" />
          <div className={`ob-step-dot ${step === 3 ? 'active' : ''}`}>3</div>
        </div>

        {/* Step 1 — Restaurant (restaurant_company) */}
        {step === 1 && (
          <div className="ob-card w-full max-w-[640px] p-10 sm:p-12 ob-fade">
            <div className="text-center mb-8">
              <div className="ob-tag">Step 01 · Restaurant</div>
              <h2 className="ob-h1 mt-3">Tell us about your restaurant</h2>
              <p className="ob-sub mt-3">
                Just the basics — you'll add a location and your owner account next.
              </p>
            </div>

            {error && <div className="ob-error mb-5">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="ob-label">Restaurant Name *</label>
                <input
                  className="ob-input"
                  name="name"
                  value={restaurantForm.name}
                  onChange={handleRestaurantChange}
                  placeholder="The Velvet Table"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="ob-label">Address</label>
                <input
                  className="ob-input"
                  name="address"
                  value={restaurantForm.address}
                  onChange={handleRestaurantChange}
                  placeholder="221 Marlowe Lane"
                />
              </div>
              <div>
                <label className="ob-label">City</label>
                <input
                  className="ob-input"
                  name="city"
                  value={restaurantForm.city}
                  onChange={handleRestaurantChange}
                  placeholder="Lisbon"
                />
              </div>
              <div>
                <label className="ob-label">Phone</label>
                <input
                  className="ob-input"
                  name="phone"
                  value={restaurantForm.phone}
                  onChange={handleRestaurantChange}
                  placeholder="+351 21 000 0000"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="ob-label">Restaurant Email (optional)</label>
                <input
                  className="ob-input"
                  type="email"
                  name="email"
                  value={restaurantForm.email}
                  onChange={handleRestaurantChange}
                  placeholder="hello@yourrestaurant.com"
                />
              </div>
            </div>

            <div className="mt-10 flex justify-end">
              <button
                className="ob-btn"
                onClick={createRestaurant}
                disabled={busy || !restaurantForm.name.trim()}
              >
                {busy ? 'Saving…' : 'Continue · Location'}
                {!busy && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Location (restaurant_location) */}
        {step === 2 && (
          <div className="ob-card w-full max-w-[640px] p-10 sm:p-12 ob-fade">
            <div className="text-center mb-8">
              <div className="ob-tag">Step 02 · Location</div>
              <h2 className="ob-h1 mt-3">Add your first location</h2>
              <p className="ob-sub mt-3">
                This becomes your <span className="text-amber-300/90">primary branch</span>. You can add more locations later.
              </p>
            </div>

            {error && <div className="ob-error mb-5">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="ob-label">Location Name *</label>
                <input
                  className="ob-input"
                  name="name"
                  value={locationForm.name}
                  onChange={handleLocationChange}
                  placeholder="Downtown · Main Branch"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="ob-label">Address</label>
                <input
                  className="ob-input"
                  name="address_line1"
                  value={locationForm.address_line1}
                  onChange={handleLocationChange}
                  placeholder="221 Marlowe Lane"
                />
              </div>
              <div>
                <label className="ob-label">City</label>
                <input
                  className="ob-input"
                  name="city"
                  value={locationForm.city}
                  onChange={handleLocationChange}
                  placeholder="Lisbon"
                />
              </div>
              <div>
                <label className="ob-label">State / Region</label>
                <input
                  className="ob-input"
                  name="state"
                  value={locationForm.state}
                  onChange={handleLocationChange}
                  placeholder="Lisboa"
                />
              </div>
              <div>
                <label className="ob-label">Postal Code</label>
                <input
                  className="ob-input"
                  name="postal_code"
                  value={locationForm.postal_code}
                  onChange={handleLocationChange}
                  placeholder="1100-001"
                />
              </div>
              <div>
                <label className="ob-label">Country</label>
                <input
                  className="ob-input"
                  name="country"
                  value={locationForm.country}
                  onChange={handleLocationChange}
                  placeholder="Portugal"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="ob-label">Location Phone</label>
                <input
                  className="ob-input"
                  name="phone"
                  value={locationForm.phone}
                  onChange={handleLocationChange}
                  placeholder="+351 21 000 0000"
                />
              </div>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button className="ob-btn-ghost" onClick={() => setStep(1)} disabled={busy}>
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <button
                className="ob-btn"
                onClick={createLocation}
                disabled={busy || !locationForm.name.trim()}
              >
                {busy ? 'Saving…' : 'Continue · Owner'}
                {!busy && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Super-Admin owner account (restaurant_user) */}
        {step === 3 && (
          <div className="ob-card w-full max-w-[640px] p-10 sm:p-12 ob-fade">
            <div className="text-center mb-8">
              <div className="ob-tag">Step 03 · Owner</div>
              <h2 className="ob-h1 mt-3">Create your owner account</h2>
              <p className="ob-sub mt-3">
                You'll be set as the <span className="text-amber-300/90">Super Admin</span> with access to all locations.
              </p>
            </div>

            {error && <div className="ob-error mb-5">{error}</div>}

            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); createUserAndFinish(); }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Decoy fields trick browsers into not autofilling the real fields below */}
              <input type="text" name="username_decoy" autoComplete="username" style={{ display: 'none' }} />
              <input type="password" name="password_decoy" autoComplete="current-password" style={{ display: 'none' }} />

              <div>
                <label className="ob-label">First Name</label>
                <input className="ob-input" name="first_name" value={userForm.first_name} onChange={handleUserChange} placeholder="Alex" autoComplete="off" />
              </div>
              <div>
                <label className="ob-label">Last Name</label>
                <input className="ob-input" name="last_name" value={userForm.last_name} onChange={handleUserChange} placeholder="Laurent" autoComplete="off" />
              </div>
              <div className="sm:col-span-2">
                <label className="ob-label">Email</label>
                <input className="ob-input" type="email" name="new_owner_email" value={userForm.email} onChange={(e) => setUserForm((p) => ({ ...p, email: e.target.value }))} placeholder="alex@yourrestaurant.com" autoComplete="off" />
              </div>
              <div>
                <label className="ob-label">Username</label>
                <input className="ob-input" name="new_owner_username" value={userForm.username} onChange={(e) => setUserForm((p) => ({ ...p, username: e.target.value }))} placeholder="alex_l" autoComplete="off" />
              </div>
              <div>
                <label className="ob-label">Password</label>
                <div className="relative">
                  <input
                    className="ob-input pr-10"
                    type={showPassword ? 'text' : 'password'}
                    name="new_owner_password"
                    value={userForm.password}
                    onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="ob-eye-btn"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Phone — country code (select) + local number */}
              <div className="sm:col-span-2">
                <label className="ob-label">Phone</label>
                <div className="flex gap-2">
                  <div className="relative">
                    <select
                      className="ob-input pr-9"
                      style={{ minWidth: '11rem' }}
                      value={userForm.country_code}
                      onChange={(e) => setUserForm((p) => ({ ...p, country_code: e.target.value }))}
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code || c.dial_code} value={c.dial_code}>
                          {c.name} ({c.dial_code})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-300/60" />
                  </div>
                  <input
                    className="ob-input flex-1"
                    name="phone"
                    value={userForm.phone}
                    onChange={(e) => setUserForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '') }))}
                    placeholder={userForm.country_code === '+230' ? '5 123 4567' : 'Local number (digits only)'}
                    autoComplete="off"
                    type="tel"
                    inputMode="tel"
                    maxLength={15}
                  />
                </div>
                {userForm.country_code === '+230' && (
                  <p className="text-xs text-white/45 mt-1.5">
                    Mauritius: 8-digit mobile (starts with 5) or 7-digit fixed-line.
                  </p>
                )}
              </div>
            </form>

            <div className="mt-10 flex items-center justify-between">
              <button className="ob-btn-ghost" onClick={() => setStep(2)} disabled={busy}>
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <button className="ob-btn" onClick={createUserAndFinish} disabled={busy}>
                {busy ? 'Finalising…' : 'Create & Enter'}
                {!busy && <ShieldCheck className="h-4 w-4" strokeWidth={2} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
